import { StaticMap as GoogleStaticMap } from '@vis.gl/react-google-maps';
import { Link } from '@janeapp/burrito-design-system';
import { useQuery } from '@tanstack/react-query';
import { getBaseUrl } from '~/env';
import { ErrorLevel, logErrorToSentry } from '~/utilities';

interface StaticMapProps {
  clinicLocationGuid: string;
  clinicName: string;
  address: string;
}

export const StaticMap = ({
  clinicLocationGuid,
  clinicName,
  address,
}: StaticMapProps) => {
  const [clinicGuid, locationId] = clinicLocationGuid.split('-');

  const { data: mapData } = useQuery({
    queryKey: ['staticMap', clinicGuid, locationId],
    queryFn: async () => {
      const baseUrl = getBaseUrl();
      const response = await fetch(
        `${baseUrl}/maps/clinic/${clinicGuid}/location/${locationId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch static map URL');
      }
      const data = await response.json();
      return data as { url: string };
    },
    throwOnError(error, _query) {
      logErrorToSentry({ type: ErrorLevel.Error, message: error.message });
      return false;
    },
    enabled: !!clinicGuid && !!locationId,
  });

  const staticMapsUrl = mapData?.url;

  if (!staticMapsUrl) return null;

  return (
    <Link
      data-testid="static-map-container"
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinicName)},${encodeURIComponent(address)}`}
      target="_blank"
    >
      <GoogleStaticMap className="h-full w-full" url={staticMapsUrl} />
    </Link>
  );
};
